import { useEffect, useRef } from 'react';
import { useInView, useReducedMotion } from 'motion/react';
import type { LottieRefCurrentProps } from 'lottie-react';

const PLAY_DELAY_MS = 80;

const lastFrame = (lottie: LottieRefCurrentProps) =>
  (lottie.getDuration(true) ?? 1) - 1;

// Shows the finished chart, then plays it once on entering view; a new replayKey
// (slide or toggle change) queues another play. Scrolling back never replays.
export const useChartPlayback = (
  active: boolean,
  loaded: boolean,
  replayKey: string,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const inView = useInView(containerRef, {
    amount: 0.12,
    margin: '0px 0px -8% 0px',
  });
  const shouldReduceMotion = useReducedMotion();
  const pendingRef = useRef(true);

  useEffect(() => {
    pendingRef.current = true;
  }, [replayKey]);

  useEffect(() => {
    const lottie = lottieRef.current;
    if (loaded && lottie) lottie.goToAndStop(lastFrame(lottie), true);
  }, [loaded]);

  useEffect(() => {
    const lottie = lottieRef.current;
    if (!loaded || !lottie || shouldReduceMotion) return;
    if (!pendingRef.current || !active || !inView) return;

    pendingRef.current = false;
    lottie.goToAndStop(0, true);
    let started = false;
    const timer = setTimeout(() => {
      started = true;
      lottie.play();
    }, PLAY_DELAY_MS);
    // A play cancelled before it starts (e.g. a quick slide change) is re-queued, not lost
    return () => {
      if (started) return;
      clearTimeout(timer);
      pendingRef.current = true;
    };
  }, [loaded, active, inView, replayKey, shouldReduceMotion]);

  return { containerRef, lottieRef };
};
